import { useEffect, useRef, useState, type ReactElement } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { rankTrackerApi } from "@/resources/rank-tracker";
import type { AdPushCutoffsResponse } from "@/types/ad-push";
import {
  AD_PUSH_META,
  DEFAULT_CUTOFFS,
  cutoffsInOrder,
  type AdPushCutoffs,
  type AdPushLabel,
} from "./adPush";

/**
 * The per-brand Ad push rules panel (AUT-497 slice B, FR-9–FR-12). A `Popover` anchored to the
 * header's `SlidersHorizontal` button; the whole trigger+content lives here so WEB-003's
 * `HeatmapHeaderRow` only has to mount this one component where its button used to be.
 *
 * **Draft, live relabel, and the single PUT (Decision D-6, the ADR).** The panel owns a local string
 * draft seeded from the brand's committed cutoffs. Editing a cutoff reclassifies the column *in the
 * browser* over the draft — `onDraftChange(validTriple)` hands the table the preview to relabel with,
 * `onDraftChange(null)` tells it to fall back to the committed values — with **no network per
 * keystroke**. Persistence is one `PUT` via `useUpdateAdPushCutoffs`, fired once on a *valid* commit
 * (Done, a valid outside-click/Escape close, or Reset-to-defaults). The prototype's per-keystroke
 * `localStorage` write does not port (`decisions/ad-push-cutoffs-commit-with-one-put-and-relabel-locally.md`).
 *
 * **Cannot close while invalid.** While the draft is non-positive/non-numeric or out of order the
 * inline error shows, Done is disabled, and the Radix `onOpenChange` close is swallowed.
 *
 * **Viewer-disabled (FR-10)** and **save-failure (FR-11)** are the two states the prototype has no
 * source for: a viewer opens and reads the panel but its inputs/Reset/Done are disabled with the
 * app's view-only copy, and a failed PUT keeps the previous cutoffs in effect and reverts the
 * column's labels to them.
 */

type Draft = Record<keyof AdPushCutoffs, string>;

const toDraft = (c: AdPushCutoffs): Draft => ({
  easeOff: String(c.easeOff),
  hold: String(c.hold),
  push: String(c.push),
});

/** The committed triple a response carries, or the defaults when the brand has no custom row. */
const committedCutoffsOf = (data: AdPushCutoffsResponse | undefined): AdPushCutoffs =>
  data ? { easeOff: data.easeOff, hold: data.hold, push: data.push } : DEFAULT_CUTOFFS;

/** The four rows, in label order. Ease off / Hold / Push are editable; Long shot is the fall-through. */
const RULES: { label: AdPushLabel; field?: keyof AdPushCutoffs; suffix?: string; text?: string }[] = [
  { label: "ease-off", field: "easeOff", suffix: "and an ad is running" },
  { label: "hold", field: "hold" },
  { label: "push", field: "push" },
  { label: "long-shot", text: "Anything worse than Push" },
];

/** Verbatim inline validation copy — the design of record (`design-input/.../sources.md § The rules panel`). */
const ERROR_NOT_POSITIVE = "Enter a positive number for every cutoff.";
const ERROR_OUT_OF_ORDER = "Cutoffs must stay in order: Ease off ≤ Hold ≤ Push.";

/** Save-failure copy (FR-11), in the app's established lifecycle-save failure voice. */
const SAVE_ERROR = "Couldn't save the Ad push rules. Please try again.";

/** Viewer-disabled copy (FR-10), reusing the app's shipped view-only phrasing (`LifecycleStateModal`). */
const VIEWER_REASON = "Your account is view-only. Ask an admin to change the Ad push rules.";

/** The prototype's last-changed date form: `Oct 12, 2026` in the viewer's locale. */
function formatChangedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const parseTriple = (draft: Draft): AdPushCutoffs => ({
  easeOff: parseFloat(draft.easeOff),
  hold: parseFloat(draft.hold),
  push: parseFloat(draft.push),
});

const isPositiveTriple = (c: AdPushCutoffs): boolean =>
  Number.isFinite(c.easeOff) &&
  Number.isFinite(c.hold) &&
  Number.isFinite(c.push) &&
  c.easeOff >= 0.1 &&
  c.hold >= 0.1 &&
  c.push >= 0.1;

const sameCutoffs = (a: AdPushCutoffs, b: AdPushCutoffs): boolean =>
  a.easeOff === b.easeOff && a.hold === b.hold && a.push === b.push;

export interface AdPushRulesPopoverProps {
  brandId: number | null | undefined;
  /** The brand's display name for the title badge. */
  brandName: string;
  /** Whether the panel is open — owned by the table shell (WEB-003's `rulesOpen`). */
  open: boolean;
  /** Request an open-state change. A close is only honoured when the draft is valid (see above). */
  onOpenChange: (open: boolean) => void;
  /** The brand's committed cutoffs + metadata from WEB-003's GET (`undefined` while loading → defaults). */
  cutoffs: AdPushCutoffsResponse | undefined;
  /** WEB-003's `isCustom` — drives the dot, the "Custom" marker and the last-changed line (FR-9, D-5). */
  isCustom: boolean;
  /** Live-relabel seam (D-6): the valid draft while editing, or `null` to fall back to committed. */
  onDraftChange: (cutoffs: AdPushCutoffs | null) => void;
}

export function AdPushRulesPopover({
  brandId,
  brandName,
  open,
  onOpenChange,
  cutoffs,
  isCustom,
  onDraftChange,
}: AdPushRulesPopoverProps): ReactElement {
  const viewerReadOnly = useViewerReadOnly();
  const updateMutation = rankTrackerApi.useUpdateAdPushCutoffs(brandId);

  const committed = committedCutoffsOf(cutoffs);
  const [draft, setDraft] = useState<Draft>(() => toDraft(committed));
  const [saveError, setSaveError] = useState<string | null>(null);

  // Whether the user has edited the draft since this open. A fresh open (open/brand change) starts
  // untouched; the first `handleChange` sets it. It gates the re-seed effect below so the user's
  // own edit is never clobbered by a late GET.
  const touchedRef = useRef(false);

  // Seed the draft from the brand's committed cutoffs each time the panel opens or the brand
  // changes (FR-12 — nothing from the previous brand carries over). A ref so the effect always
  // reads the latest committed triple without re-seeding on every unrelated render.
  const committedRef = useRef(committed);
  committedRef.current = committed;
  useEffect(() => {
    if (!open) return;
    touchedRef.current = false;
    setDraft(toDraft(committedRef.current));
    setSaveError(null);
    onDraftChange(null);
    // onDraftChange is a stable setter from the parent; re-seed only on open/brand change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brandId]);

  // Re-seed while untouched: if the GET resolves *after* the panel opened, `committed` changes from
  // the defaults to the brand's real custom cutoffs. Re-seed the still-untouched draft to them so an
  // outside click is a no-op (no PUT, no history row) instead of overwriting the custom cutoffs with
  // defaults (reviewer comment 3, data loss). Once the user edits (touchedRef=true), re-seeding stops
  // and their edit is preserved.
  useEffect(() => {
    if (!open || touchedRef.current) return;
    setDraft(toDraft(committedRef.current));
    setSaveError(null);
    onDraftChange(null);
    // Keyed on the committed triple so a resolved GET re-seeds; onDraftChange is a stable parent setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brandId, committed.easeOff, committed.hold, committed.push]);

  const parsed = parseTriple(draft);
  const numeric = isPositiveTriple(parsed);
  const inOrder = numeric && cutoffsInOrder(parsed);
  const valid = numeric && inOrder;
  const changed = valid && !sameCutoffs(parsed, committed);

  // Edit a cutoff: update the local draft and, when the new draft is valid, hand the table the
  // preview to relabel with (D-6). While invalid, the column falls back to the committed values.
  const handleChange = (field: keyof AdPushCutoffs, value: string): void => {
    if (viewerReadOnly) return;
    touchedRef.current = true;
    const next: Draft = { ...draft, [field]: value };
    setDraft(next);
    setSaveError(null);
    const nextParsed = parseTriple(next);
    onDraftChange(isPositiveTriple(nextParsed) && cutoffsInOrder(nextParsed) ? nextParsed : null);
  };

  // The single PUT (D-6). Fires once with the given triple; on success clears the preview and runs
  // `onDone`; on failure shows the save-failure message, reverts the column to the committed cutoffs
  // and keeps the panel open so the attempt can be corrected or retried.
  const commit = (triple: AdPushCutoffs, onDone: () => void): void => {
    if (!brandId) return;
    updateMutation.mutate(triple, {
      onSuccess: () => {
        setSaveError(null);
        onDraftChange(null);
        onDone();
      },
      onError: () => {
        setSaveError(SAVE_ERROR);
        onDraftChange(null);
      },
    });
  };

  // A close request (Done, outside click, Escape, or toggling the trigger). Blocked while invalid or
  // while a save is in flight. A valid change commits once, then closes on success; a pristine or
  // unchanged draft closes with no PUT (FR-9, D-6).
  const requestClose = (): void => {
    if (!valid || updateMutation.isPending) return;
    if (!changed) {
      onDraftChange(null);
      onOpenChange(false);
      return;
    }
    commit(parsed, () => onOpenChange(false));
  };

  const handleOpenChange = (next: boolean): void => {
    if (next) {
      onOpenChange(true);
      return;
    }
    requestClose();
  };

  // Reset to defaults (FR-7): the same commit path with 3.5 / 10 / 30. Shows the default triple in
  // the inputs, previews it, and PUTs it once (unless already on defaults). Keeps the panel open.
  const handleReset = (): void => {
    if (viewerReadOnly || updateMutation.isPending) return;
    setDraft(toDraft(DEFAULT_CUTOFFS));
    setSaveError(null);
    if (sameCutoffs(DEFAULT_CUTOFFS, committed)) {
      onDraftChange(null);
      return;
    }
    onDraftChange(DEFAULT_CUTOFFS);
    commit(DEFAULT_CUTOFFS, () => undefined);
  };

  const controlsDisabled = viewerReadOnly || updateMutation.isPending;
  const lastChangedBy = cutoffs?.lastChangedBy ?? null;
  const lastChangedAt = cutoffs?.lastChangedAt ?? null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-6 w-6 rounded-full"
          aria-label="Edit Ad push rules"
          aria-expanded={open}
          onClick={(e) => e.stopPropagation()}
        >
          <SlidersHorizontal className="!size-3.5" />
          {isCustom && (
            <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-[hsl(300_42%_45%)] dark:bg-[hsl(300_45%_62%)]" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={8}
        className="w-[440px] max-h-[var(--radix-popover-content-available-height)] space-y-4 overflow-y-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <p className="text-base font-semibold text-foreground">Ad push rules</p>
          {brandName && <Badge variant="secondary">{brandName}</Badge>}
          {isCustom && <span className="text-xs text-muted-foreground">Custom</span>}
        </div>
        <p className="text-sm text-muted-foreground">
          Based on 7-day average organic rank. Applies to every keyword for this brand.
        </p>

        <div className="divide-y divide-border">
          {RULES.map((rule) => {
            const meta = AD_PUSH_META[rule.label];
            const Icon = meta.icon;
            return (
              <div key={rule.label} className="space-y-1 py-3 first:pt-0 last:pb-0">
                <div className="flex items-center gap-3">
                  <Badge
                    variant="secondary"
                    className={cn("w-24 shrink-0 justify-center gap-1", meta.badgeClass)}
                  >
                    <Icon className="h-3 w-3 shrink-0" />
                    {meta.name}
                  </Badge>
                  {rule.field ? (
                    <span className="flex items-center gap-2 whitespace-nowrap text-sm text-foreground">
                      Organic avg ≤
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="9999.9"
                        value={draft[rule.field]}
                        onChange={(e) => handleChange(rule.field!, e.target.value)}
                        disabled={controlsDisabled}
                        title={viewerReadOnly ? VIEWER_REASON : undefined}
                        className="h-8 w-16 px-2 text-right tabular-nums"
                        aria-label={`${meta.name} cutoff`}
                      />
                      {rule.suffix}
                    </span>
                  ) : (
                    <span className="text-sm text-foreground">{rule.text}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{meta.meaning}</p>
              </div>
            );
          })}
        </div>

        {!valid && (
          <p className="text-xs text-destructive">
            {numeric ? ERROR_OUT_OF_ORDER : ERROR_NOT_POSITIVE}
          </p>
        )}

        {saveError && <p className="text-xs text-destructive">{saveError}</p>}

        {viewerReadOnly && <p className="text-xs text-muted-foreground">{VIEWER_REASON}</p>}

        <p className="text-xs text-muted-foreground">
          * These labels are suggestions based on rank only. They don&apos;t account for ad spend,
          conversion or profit.
        </p>

        <div className="space-y-2 border-t border-border pt-3">
          {isCustom && lastChangedBy && (
            <p className="text-xs text-muted-foreground">
              Last changed by {lastChangedBy}
              {lastChangedAt ? `, ${formatChangedAt(lastChangedAt)}` : ""}
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Defaults: {DEFAULT_CUTOFFS.easeOff} / {DEFAULT_CUTOFFS.hold} / {DEFAULT_CUTOFFS.push}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={handleReset}
              disabled={controlsDisabled}
              title={viewerReadOnly ? VIEWER_REASON : undefined}
            >
              Reset to defaults
            </Button>
            <Button
              size="sm"
              onClick={() => requestClose()}
              disabled={!valid || controlsDisabled}
              title={viewerReadOnly ? VIEWER_REASON : undefined}
            >
              Done
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
